"use client"

import { useCallback, useEffect, useState } from "react"
import { motion } from "framer-motion"
import {
  Calendar,
  ExternalLink,
  TrendingUp,
  Users,
  Music,
  Zap,
  Award,
  MessageCircle,
  ArrowRight,
  Heart,
} from "lucide-react"
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants"
import { generateMetadata } from "../seo/metadataGenerator"
import { generateBreadcrumbSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator"
import SeoHead from "../seo/SeoHead"
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function CommunityUpdatesPage() {
  usePrerenderReady();
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const updateCursorPosition = useCallback((e) => {
    requestAnimationFrame(() => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  useEffect(() => {
    document.body.style.cursor = "none";
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const interactiveElements = document.querySelectorAll(
      'a, button, input, [role="button"]'
    );
    interactiveElements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    window.addEventListener("mousemove", updateCursorPosition);

    // Simulate loading time (remove this in production and use real loading logic)
    // const timer = setTimeout(() => {
    //   setIsLoading(false);
    // }, 3000);

    return () => {
      // clearTimeout(timer);
      document.body.style.cursor = "auto";
      window.removeEventListener("mousemove", updateCursorPosition);
      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [updateCursorPosition]);

  const categories = [
    { id: "all", label: "All Updates", count: 24 },
    { id: "product", label: "Product Updates", count: 8 },
    // { id: "success", label: "Success Stories", count: 6 },
    { id: "community", label: "Community", count: 5 },
    { id: "industry", label: "Industry News", count: 5 },
  ]

  const updates = [
    {
      id: 1,
      category: "product",
      title: "Introducing Amplitude AI 2.0: Your Smartest Music Career Assistant Yet",
      excerpt:
        "Our latest AI update learns from your unique style and automatically optimizes campaigns across all platforms. See 3x better results with zero extra effort.",
      date: "2025-01-15",
      readTime: "4 min read",
      image: require('../assets/images/UpNComer Main Logo.png'),
      link: "https://blog.upncomer.com/amplitude-ai-2-0-launch",
      featured: true,
      stats: {
        views: "2.4K",
        likes: 89,
        comments: 23,
      },
      tags: ["AI", "Product Launch", "Automation"],
    },
    {
      id: 2,
      category: "success",
      title: "From 500 to 50K Monthly Listeners: Maya's Journey with UpNComer",
      excerpt:
        "Independent R&B artist Maya Chen shares how she grew her fanbase 100x in 6 months using our AI marketing tools while keeping 100% of her royalties.",
      date: "2025-01-12",
      readTime: "6 min read",
      image: require('../assets/images/dashboard-full.png'),
      link: "https://blog.upncomer.com/maya-chen-success-story",
      featured: false,
      stats: {
        views: "1.8K",
        likes: 156,
        comments: 34,
      },
      tags: ["Success Story", "R&B", "Growth"],
    },
    {
      id: 3,
      category: "product",
      title: "New Feature: Smart Playlist Targeting Now Live",
      excerpt:
        "Our AI now identifies the perfect playlists for your music and automatically handles the pitching process. Early users are seeing 40% higher acceptance rates.",
      date: "2025-01-10",
      readTime: "3 min read",
      image: require('../assets/images/UpNComer Main Logo.png'),
      link: "https://blog.upncomer.com/smart-playlist-targeting",
      featured: false,
      stats: {
        views: "3.1K",
        likes: 67,
        comments: 18,
      },
      tags: ["Playlists", "AI", "Feature Update"],
    },
    {
      id: 4,
      category: "community",
      title: "UpNComer Artists Collective: January Spotlight",
      excerpt:
        "Meet 5 incredible independent artists who are crushing it this month. From lo-fi hip-hop to electronic dance, our community is diverse and thriving.",
      date: "2025-01-08",
      readTime: "5 min read",
      image: require('../assets/images/dashboard-full.png'),
      link: "https://blog.upncomer.com/january-artist-spotlight",
      featured: false,
      stats: {
        views: "1.2K",
        likes: 94,
        comments: 41,
      },
      tags: ["Community", "Artist Spotlight", "Collective"],
    },
    {
      id: 5,
      category: "industry",
      title: "The State of Independent Music in 2025: What You Need to Know",
      excerpt:
        "Streaming revenue is up 23%, but competition is fierce. Here's how independent artists can thrive in the new landscape with the right tools and strategy.",
      date: "2025-01-05",
      readTime: "8 min read",
      image: require('../assets/images/UpNComer Main Logo.png'),
      link: "https://blog.upncomer.com/independent-music-2025-report",
      featured: false,
      stats: {
        views: "4.7K",
        likes: 203,
        comments: 67,
      },
      tags: ["Industry Report", "Streaming", "Strategy"],
    },
    {
      id: 6,
      category: "success",
      title: "How DJ Luna Went Viral on TikTok Using Our Content AI",
      excerpt:
        "Electronic artist DJ Luna's track exploded on TikTok after using our AI content generator. 2M views in 48 hours and counting.",
      date: "2025-01-03",
      readTime: "4 min read",
      image: require('../assets/images/dashboard-full.png'),
      link: "https://blog.upncomer.com/dj-luna-tiktok-viral",
      featured: false,
      stats: {
        views: "5.2K",
        likes: 312,
        comments: 89,
      },
      tags: ["TikTok", "Viral", "Electronic", "Content AI"],
    },
    {
      id: 7,
      category: "product",
      title: "Advanced Analytics Dashboard: Now with Predictive Insights",
      excerpt:
        "See not just what happened, but what's likely to happen next. Our new predictive analytics help you make smarter decisions about releases and campaigns.",
      date: "2024-12-28",
      readTime: "5 min read",
      image: require('../assets/images/UpNComer Main Logo.png'),
      link: "https://blog.upncomer.com/predictive-analytics-launch",
      featured: false,
      stats: {
        views: "2.9K",
        likes: 78,
        comments: 25,
      },
      tags: ["Analytics", "Predictions", "Data Science"],
    },
    {
      id: 8,
      category: "community",
      title: "UpNComer Holiday Compilation: 20 Artists, One Amazing Album",
      excerpt:
        "Our community came together to create something magical. Stream the UpNComer Holiday Compilation featuring 20 independent artists from around the world.",
      date: "2024-12-20",
      readTime: "3 min read",
      image: require('../assets/images/dashboard-full.png'),
      link: "https://blog.upncomer.com/holiday-compilation-2024",
      featured: false,
      stats: {
        views: "1.9K",
        likes: 145,
        comments: 52,
      },
      tags: ["Compilation", "Holiday", "Community Project"],
    },
  ]

  const filteredUpdates =
    selectedCategory === "all" ? updates : updates.filter((update) => update.category === selectedCategory)

  const featuredUpdate = updates.find((update) => update.featured)
  const regularUpdates = updates.filter((update) => !update.featured)

  //SEO
  const metadata = generateMetadata({
    title:
      "Music Community News & Platform Updates | UpNComer",

    description:
      "Stay updated with the latest platform announcements, artist community news, feature releases, music industry insights and product improvements from UpNComer. Discover new tools, growth opportunities, educational resources and updates designed to help independent artists grow careers and navigate the evolving music industry.",

    image: PAGE_OG_IMAGES.communityUpdates,

    path: "/community-updates",
  })

  const webPageSchema = generateWebPageSchema({
    title:
      "Music Community News & Platform Updates | UpNComer",

    description:
      "Stay updated with the latest platform announcements, artist community news, feature releases, music industry insights and product improvements from UpNComer. Discover new tools, growth opportunities, educational resources and updates designed to help independent artists grow careers and navigate the evolving music industry.",

    url: `${SITE_URL}/community-updates`,
  })

  const breadcrumbItems = [
    {
      "name": "Home",
      "path": "/"
    },
    {
      "name": "Community Updates",
      "path": "/community-updates"
    }
  ]

  const schemas = generateGraphSchema([
    webPageSchema,
    generateBreadcrumbSchema(breadcrumbItems)
  ])

  return (
    <>
      <SeoHead
        metadata={metadata}
        schemas={schemas}
      />
      <div
        className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%)`,
        }}
      >
        <div className="inner-circle"></div>
      </div>
      <div
        className={`transition-all duration-1000 opacity-100 blur-none`}
      >
        <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
          {/* Enhanced Multi-layered Background */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

            {/* Floating orbs */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 10,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              }}
              className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
            />

            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: 12,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
                delay: 3,
              }}
              className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
            />
          </div>

          <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
            <div className="container max-w-6xl mx-auto">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                  Community Updates
                </h1>
                <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed mb-8">
                  Stay connected with the latest product updates, success stories, and industry insights from the UpNComer
                  community
                </p>

                {/* Newsletter Signup */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-6 max-w-md mx-auto"
                  style={{
                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                  }}
                >
                  <div className="flex items-center justify-center mb-4">
                    <MessageCircle className="w-6 h-6 text-[#FA6138] mr-2" />
                    <span className="text-[#c7d2fe] font-medium">Never Miss an Update</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-2 bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-lg text-[#c7d2fe] placeholder-[#818cf8]/60 focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 text-sm"
                    />
                    <button className="px-4 py-2 bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white rounded-lg hover:from-[#e55532] hover:to-[#FA6138] transition-all duration-300 text-sm font-medium">
                      Subscribe
                    </button>
                  </div>
                </motion.div>
              </motion.div>

              {/* Category Filter */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-wrap justify-center gap-3 mb-12"
              >
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${selectedCategory === category.id
                      ? "bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white shadow-lg"
                      : "bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40"
                      } backdrop-blur-xl`}
                  >
                    {category.label} ({category.count})
                  </button>
                ))}
              </motion.div>

              {/* Featured Update */}
              {featuredUpdate && selectedCategory === "all" && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="mb-16"
                >
                  <div
                    className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl overflow-hidden group hover:border-[#a5b4fc]/30 transition-all duration-500"
                    style={{
                      boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                    }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div className="relative overflow-hidden">
                        <img
                          src={featuredUpdate.image || "/placeholder.svg"}
                          alt={featuredUpdate.title}
                          className="w-full h-64 lg:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white px-3 py-1 rounded-full text-xs font-medium">
                            FEATURED
                          </span>
                        </div>
                      </div>
                      <div className="p-8 flex flex-col justify-center">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-[#FA6138] text-sm font-medium uppercase tracking-wide">
                            {featuredUpdate.category}
                          </span>
                          <div className="flex items-center text-[#818cf8]/60 text-sm">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(featuredUpdate.date).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>
                        </div>
                        <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4 leading-tight">
                          {featuredUpdate.title}
                        </h2>
                        <p className="text-[#818cf8]/80 mb-6 leading-relaxed">{featuredUpdate.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6 text-sm text-[#60a5fa]/80">
                            <span className="flex items-center">
                              <TrendingUp className="w-4 h-4 mr-1" />
                              {featuredUpdate.stats.views}
                            </span>
                            <span className="flex items-center">
                              <Heart className="w-4 h-4 mr-1" />
                              {featuredUpdate.stats.likes}
                            </span>
                            <span className="flex items-center">
                              <MessageCircle className="w-4 h-4 mr-1" />
                              {featuredUpdate.stats.comments}
                            </span>
                          </div>
                          <a
                            href={featuredUpdate.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white px-6 py-3 rounded-lg hover:from-[#e55532] hover:to-[#FA6138] transition-all duration-300 font-medium group"
                          >
                            Read Full Story
                            <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Updates Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredUpdates
                  .filter((update) => !update.featured || selectedCategory !== "all")
                  .map((update, index) => (
                    <motion.div
                      key={update.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                      className="bg-gradient-to-br from-[#1e1b4b]/20 via-[#312e81]/15 to-[#4338ca]/10 backdrop-blur-xl border border-[#4f46e5]/20 rounded-2xl overflow-hidden group hover:border-[#a5b4fc]/30 hover:from-[#6366f1]/15 hover:to-[#1e1b4b]/20 transition-all duration-500"
                      style={{
                        boxShadow: "0 10px 30px rgba(79, 70, 229, 0.15), inset 0 1px 0 rgba(165, 180, 252, 0.1)",
                      }}
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={update.image || "/placeholder.svg"}
                          alt={update.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="bg-[#1e3a8a]/80 backdrop-blur-sm text-[#93c5fd] px-2 py-1 rounded-full text-xs font-medium border border-[#3b82f6]/30">
                            {update.category.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center text-[#818cf8]/60 text-sm">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(update.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                          <span className="text-[#60a5fa]/60 text-sm">{update.readTime}</span>
                        </div>

                        <h3 className="text-lg font-semibold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-3 leading-tight group-hover:from-[#a5b4fc] group-hover:to-[#818cf8] transition-all duration-300">
                          {update.title}
                        </h3>

                        <p className="text-[#818cf8]/70 text-sm mb-4 leading-relaxed">{update.excerpt}</p>

                        <div className="flex flex-wrap gap-2 mb-4">
                          {update.tags.slice(0, 2).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="bg-[#3b82f6]/20 text-[#93c5fd] px-2 py-1 rounded text-xs border border-[#60a5fa]/20"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-xs text-[#60a5fa]/60">
                            <span className="flex items-center">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              {update.stats.views}
                            </span>
                            <span className="flex items-center">
                              <Heart className="w-3 h-3 mr-1" />
                              {update.stats.likes}
                            </span>
                          </div>
                          <a
                            href={update.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-[#FA6138] hover:text-[#e55532] transition-colors text-sm font-medium group"
                          >
                            Read More
                            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>

              {/* Load More */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="text-center mt-16"
              >
                <button className="bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] px-8 py-3 rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40 transition-all duration-300 backdrop-blur-xl font-medium">
                  Load More Updates
                </button>
              </motion.div>

              {/* Community Stats */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.4 }}
                className="mt-20"
              >
                <div
                  className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8"
                  style={{
                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                  }}
                >
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent text-center mb-8">
                    Community Pulse
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                      { icon: <Users className="w-6 h-6" />, value: "450+", label: "Active Artists" },
                      { icon: <Music className="w-6 h-6" />, value: "2.3M+", label: "Streams This Month" },
                      { icon: <Award className="w-6 h-6" />, value: "89%", label: "Success Rate" },
                      { icon: <Zap className="w-6 h-6" />, value: "24/7", label: "AI Working" },
                    ].map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="flex justify-center mb-3 text-[#FA6138]">{stat.icon}</div>
                        <div className="text-2xl font-bold text-[#c7d2fe] mb-1">{stat.value}</div>
                        <div className="text-[#818cf8]/70 text-sm">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
